import { Inject, Injectable, NgZone } from '@angular/core';
import { GoogleAnalyticsService, NEWSLETTER_OPT_IN_ENDPOINT, WindowService } from '@candyland/angular/shared/util-nmo';
import type { FunnelDataModel } from '@candyland/angular/shared/data-access-nmo';
import {
  DriveTrackingService,
  KilkayaSalesTrackingService,
  EmarsysService,
} from '@candyland/angular/shared/data-access-nmo';
import { HttpClient } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { type Observable, Subject, filter } from 'rxjs';
import type {
  PianoCallbackEvent,
  PianoCompleteCheckout,
  PianoScript,
  PianoStartCheckout,
  PianoTerm,
  PianoUser,
  PianoUserResponse,
  PianoUserUpdateParams,
} from '@candyland/angular/shared/model-nmo';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ProfileDataService } from '@candyland/angular/shared/oauth2/feature';
import { ChannelOrigin } from '@candyland/generic/shared/article-tt-com/model';

export const pianoCheckoutContinuationKey = 'piano_checkout_continuation';
export const pianoCheckoutAbandonmentCookieName = '_pc_checkoutAbandon';

@Injectable({ providedIn: 'root' })
export class PianoAppCallbacksService {
  private pianoPostMessages$ = new Subject<PianoEventData>();

  constructor(
    private _ngZone: NgZone,
    private googleAnalyticsService: GoogleAnalyticsService,
    private kilkayaSalesTrackingService: KilkayaSalesTrackingService,
    private windowService: WindowService,
    private driveTrackingService: DriveTrackingService,
    private emarsysService: EmarsysService,
    private http: HttpClient,
    private oAuthService: OAuthService,
    private profileDataService: ProfileDataService,
    @Inject(NEWSLETTER_OPT_IN_ENDPOINT) private newsletterOptInEndpoint: string
  ) {
    if (this.windowService.isBrowser) {
      // Listen to post messages from Piano
      window.addEventListener('message', (event) => {
        // Only accept messages from Piano (Stage and live Piano endpoints)
        if (
          !event.origin.includes('tinypass.com') &&
          !event.origin.includes('buy-eu.piano.io') &&
          !event.origin.includes('id-eu.piano.io')
        ) {
          return;
        }

        try {
          const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          // Show Piano logs in console if enabled
          if (localStorage.getItem('enablePianoLogs') === 'true') {
            console.log('Piano post message:', eventData);
          }

          this.applyGeneralPianoPostMessage(eventData);
          this.pianoPostMessages$.next(eventData);
        } catch (error) {
          console.error('Error processing Piano post message:', error);
        }
      });
    }
  }

  public pianoPostMessages(query?: string): Observable<PianoEventData> {
    if (query) {
      return this.pianoPostMessages$.asObservable().pipe(filter((eventData) => eventData.event === query));
    } else {
      return this.pianoPostMessages$.asObservable();
    }
  }

  public syncUserData(trigger: 'form' | 'init' = 'init'): void {
    this.profileDataService.getPianoUserData().subscribe({
      next: (profile: PianoUser) => {
        const { first_name: firstName, last_name: lastName, custom_fields: customFields, email, uid } = profile;

        const customFirstName = customFields.find((field) => field.fieldName === 'first-name')?.value;
        const customLastName = customFields.find((field) => field.fieldName === 'last-name')?.value;

        const dataToUpdate: Partial<PianoUserUpdateParams> = this.getDataToUpdate(
          trigger,
          firstName,
          lastName,
          customFirstName,
          customLastName
        );

        if (Object.keys(dataToUpdate).length) {
          const data: PianoUserUpdateParams = {
            ...dataToUpdate,
            email,
            uid,
          };

          console.log('Updating user data:', data);

          this.profileDataService.changePianoUserFormData(data).subscribe({
            error: (error) => {
              console.error('Problem while updating user data', error);
            },
          });
        }
      },
      error: (error) => {
        console.error('Problem while syncing user data', error);
      },
    });
  }

  public handleAbandonedCart(termId: string, expireInDays: number): void {
    const date = new Date();
    date.setTime(date.getTime() + expireInDays * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();

    document.cookie = pianoCheckoutAbandonmentCookieName + '=' + termId + ';' + expires + ';path=/';
  }

  public clearAbandonedCart(): void {
    document.cookie = pianoCheckoutAbandonmentCookieName + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  public addTrackConversionHandler(): void {
    this._ngZone.runOutsideAngular((): void => {
      window.tp.push([
        'addHandler',
        'startCheckout',
        (termData: PianoStartCheckout): void => {
          this.driveFunnelEntry(termData);
          this.handleAbandonedCart(termData.termId, 21);
        },
      ]);
      window.tp.push([
        'addHandler',
        'checkoutComplete',
        (conversion: PianoCompleteCheckout): void => {
          this.subscribeToMorningNewsletter(conversion);
          this.datalayerPushEvent(conversion);
          this.kilkayaSalesTracking(conversion);
          this.driveFunnelConversion(conversion);
          this.clearAbandonedCart();
        },
      ]);
    });
  }

  public emarsysTracking(): void {
    this._ngZone.runOutsideAngular(() => {
      window.tp.push([
        'addHandler',
        'checkoutCustomEvent',
        ({ eventName, params: data }: PianoCallbackEvent) => {
          if (eventName === 'tt-startCheckout') {
            this.emarsysTrackingStartCheckoutEvent(data);
          }
        },
      ]);

      window.tp.push([
        'addHandler',
        'checkoutComplete',
        (conversion: PianoCompleteCheckout) => {
          this.emarsysTrackingCheckoutCompleteEvent(conversion);
        },
      ]);
    });
  }

  private emarsysTrackingStartCheckoutEvent(data: { params: string }): void {
    this.pianoPostMessages('startCheckout').subscribe((_) => {
      window.tp.api.callApi('/anon/user/get', {}, (userData: PianoUserResponse) => {
        const termData: PianoTerm = typeof data.params === 'string' ? JSON.parse(data.params) : data.params;
        if (userData.user?.email) {
          this.emarsysService.pushEmailToScarabQueue(userData.user.email);
        }

        this.emarsysService.pushCategoryToScarabQueue();
        this.emarsysService.pushPianoCartToScarabQueue({
          item: termData.termId,
          price: termData.chargeAmount,
        });
        this.emarsysService.pushGoToScarabQueue();
      });
    });
  }

  private emarsysTrackingCheckoutCompleteEvent(conversion: PianoCompleteCheckout) {
    this.emarsysService.pushEmailToScarabQueue(conversion.email);
    this.emarsysService.pushPianoPurchaseToScarabQueue({
      orderId: conversion.paymentId,
      item: conversion.termId,
      price: conversion.chargeAmount,
      termConversionId: conversion.termConversionId,
    });
    this.emarsysService.pushGoToScarabQueue();
  }

  private datalayerPushEvent(conversion: PianoCompleteCheckout): void {
    if (!this.windowService.isBrowser) {
      return;
    }

    const articleRegex = /()*\/(artikel)\/([0-9]{8})/;
    const url: string = window.document.URL;
    const articleId: string = articleRegex.exec(url) !== null ? articleRegex.exec(url)[3] : 'shop';

    this.googleAnalyticsService.pushEvent({
      event: 'pageEvent',
      eventType: 'success',
      pageCategory: 'Kauf Resultat',
      productTyp: conversion.termId,
      price: conversion.chargeAmount,
      paymentTyp: conversion.paymentMethodName,
      offerId: conversion.offerId,
      articleId: articleId,
    });
  }

  private kilkayaSalesTracking(conversion: PianoCompleteCheckout): void {
    if (conversion.chargeAmount.valueOf() === 0) {
      return;
    }
    this.kilkayaSalesTrackingService.trackSales(
      window.document.URL,
      this.windowService.checkIsDesktop(),
      window.document.title,
      conversion.termId
    );
  }

  private driveFunnelConversion(conversion: PianoCompleteCheckout): void {
    if (ChannelOrigin.TtCom === document.location.origin) {
      const data: FunnelDataModel = {
        action: 'conversion',
        label: 'thankyou-page',
        product: conversion.termId,
        price: `${conversion.chargeAmount}`,
        payment_method: conversion.paymentMethodName,
      };
      this.driveTrackingService.trackDriveFunnel(data);
    }
  }

  private subscribeToMorningNewsletter(conversion: PianoCompleteCheckout): void {
    const url = `${this.newsletterOptInEndpoint}/subscribe/ttammorgen/${conversion.uid}/true`;
    this.http.post(url, null, { headers: { Authorization: this.oAuthService.authorizationHeader() } }).subscribe({
      next: () => {
        console.log('user is successfully subscribed to TT am Morgen');
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  private driveFunnelEntry(startobj: PianoStartCheckout): void {
    if (ChannelOrigin.TtCom === document.location.origin) {
      const label = startobj.containerSelector === '#piano-logwall' ? 'paywall' : 'direct';
      const data: FunnelDataModel = {
        action: 'funnel-entry',
        label: label,
        product: startobj.termId,
        price: null,
        payment_method: null,
      };
      this.driveTrackingService.trackDriveFunnel(data);
    }
  }

  private applyGeneralPianoPostMessage(data: PianoEventData): void {
    if (data.event === 'pianoIdEvent') {
      // For cases when we have forms inside Piano checkout and then finish the checkout
      if (data.params.event === 'cfFormSubmission') {
        this.syncUserData('form');
      }
    } else if (data.event === 'formSend') {
      this.syncUserData('form');
    }
  }

  private getDataToUpdate(
    trigger: 'form' | 'init',
    firstName: string,
    lastName: string,
    customFirstName: string,
    customLastName: string
  ): Partial<PianoUserUpdateParams> {
    const dataToUpdate: Partial<PianoUserUpdateParams> = {};

    if (trigger === 'form') {
      if (customFirstName) {
        dataToUpdate.first_name = customFirstName;
      }
      if (customLastName) {
        dataToUpdate.last_name = customLastName;
      }
    } else {
      if (firstName && (!customFirstName || customFirstName !== firstName)) {
        dataToUpdate.custom_fields = { ...dataToUpdate.custom_fields, 'first-name': firstName };
      }
      if (lastName && (!customLastName || customLastName !== lastName)) {
        dataToUpdate.custom_fields = { ...dataToUpdate.custom_fields, 'last-name': lastName };
      }
    }

    return dataToUpdate;
  }
}

declare global {
  interface Window {
    tp: PianoScript;
  }
}

interface PianoEventData {
  source?: string;
  event: string;
  params: Record<string, unknown>;
}
